import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { tenantUsersAPI, rolePermissionAPI, staffAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from '../components/ContentHeader';
import './TenantTeam.css';

function compareStaffByName(a, b) {
  const la = (a.last_name || '').trim().toLocaleLowerCase();
  const lb = (b.last_name || '').trim().toLocaleLowerCase();
  if (la !== lb) return la.localeCompare(lb);
  const fa = (a.first_name || '').trim().toLocaleLowerCase();
  const fb = (b.first_name || '').trim().toLocaleLowerCase();
  return fa.localeCompare(fb);
}

/** Staff `<option>` label: "Last, First (email)" for directory-style reading and A–Z by last name. */
function formatStaffSelectLabel(s) {
  const last = (s.last_name || '').trim();
  const first = (s.first_name || '').trim();
  const name =
    last && first ? `${last}, ${first}` : last || first || `Staff #${s.id}`;
  return s.email ? `${name} (${s.email})` : name;
}

/**
 * Tenant-level user/role management. Lists users in the tenant and lets admins add/remove roles (stackable).
 * Requires settings.assign_roles (ADMIN / MANAGER).
 */
function TenantTeam() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingForUser, setAddingForUser] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [permsUser, setPermsUser] = useState(null);
  const [effectivePerms, setEffectivePerms] = useState(null);
  const [permsLoading, setPermsLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserEmail, setAddUserEmail] = useState('');
  const [addUserRoleId, setAddUserRoleId] = useState('');
  const [addUserStaffId, setAddUserStaffId] = useState('');
  const [staff, setStaff] = useState([]);

  const canAssign = hasPermission('settings.assign_roles');

  useEffect(() => {
    if (!canAssign) return;
    loadData();
  }, [canAssign]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersRes, rolesRes, staffRes] = await Promise.all([
        tenantUsersAPI.getList({ staff_only: 'true' }).catch(() => ({ data: { data: [] } })),
        rolePermissionAPI.getAllRoles().catch(() => ({ data: { data: [] } })),
        staffAPI.getAll({ active: 'true' }).catch(() => ({ data: { data: [] } })),
      ]);
      setUsers(usersRes.data?.data || []);
      setRoles(rolesRes.data?.data || []);
      const staffRows = staffRes.data?.data || [];
      setStaff([...staffRows].sort(compareStaffByName));
    } catch (err) {
      console.error('Error loading team data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    const email = addUserEmail.trim();
    if (!email || !addUserRoleId || !addUserStaffId) return;
    try {
      setSaving(true);
      setError(null);
      await tenantUsersAPI.addUser({
        email,
        role_id: parseInt(addUserRoleId, 10),
        staff_id: parseInt(addUserStaffId, 10),
      });
      await loadData();
      setShowAddUser(false);
      setAddUserEmail('');
      setAddUserRoleId('');
      setAddUserStaffId('');
    } catch (err) {
      console.error('Error adding user:', err);
      setError(err.response?.data?.error || 'Failed to add user');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRole = async (userId) => {
    if (!selectedRoleId) return;
    try {
      setSaving(true);
      setError(null);
      await tenantUsersAPI.addRole(userId, parseInt(selectedRoleId, 10));
      await loadData();
      setAddingForUser(null);
      setSelectedRoleId('');
    } catch (err) {
      console.error('Error adding role:', err);
      setError(err.response?.data?.error || 'Failed to add role');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (userId, roleId) => {
    if (!window.confirm('Remove this role from the user?')) return;
    try {
      setSaving(true);
      setError(null);
      await tenantUsersAPI.removeRole(userId, roleId);
      await loadData();
    } catch (err) {
      console.error('Error removing role:', err);
      setError(err.response?.data?.error || 'Failed to remove role');
    } finally {
      setSaving(false);
    }
  };

  const handleViewEffectivePermissions = async (user) => {
    setPermsUser(user);
    setEffectivePerms(null);
    setPermsLoading(true);
    try {
      const res = await tenantUsersAPI.getEffectivePermissions(user.id);
      setEffectivePerms(res.data?.data || null);
    } catch (err) {
      console.error('Error loading effective permissions:', err);
      setEffectivePerms({ error: err.response?.data?.error || 'Failed to load' });
    } finally {
      setPermsLoading(false);
    }
  };

  const closePermsModal = () => {
    setPermsUser(null);
    setEffectivePerms(null);
  };

  if (!canAssign) {
    navigate(tenantPath('/settings'), { replace: true });
    return null;
  }

  if (loading) {
    return <div className="tenant-team-loading">Loading team…</div>;
  }

  return (
    <div className="tenant-team">
      <ContentHeader
        title="Team – Users & roles"
        titleHint="Only users linked to a staff member in this tenant are shown. Add a user by selecting a staff member (create them under Settings → Staff first if needed), then email and role."
        actions={
          !showAddUser && (
            <button
              type="button"
              className="btn btn-primary tenant-team-add-user-btn"
              onClick={() => setShowAddUser(true)}
              disabled={saving}
            >
              + Add user
            </button>
          )
        }
      />
      <div className="tenant-team-add-user-row">
          {showAddUser ? (
            <form className="tenant-team-add-user-form" onSubmit={handleAddUserSubmit}>
              <input
                type="email"
                placeholder="Email"
                value={addUserEmail}
                onChange={(e) => setAddUserEmail(e.target.value)}
                required
                disabled={saving}
                className="tenant-team-add-user-email"
              />
              <select
                value={addUserRoleId}
                onChange={(e) => setAddUserRoleId(e.target.value)}
                required
                disabled={saving}
                className="tenant-team-add-user-role"
              >
                <option value="">Select role…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name || r.code}</option>
                ))}
              </select>
              <select
                value={addUserStaffId}
                onChange={(e) => setAddUserStaffId(e.target.value)}
                disabled={saving}
                className="tenant-team-add-user-staff"
                title="Required: staff member to link (create under Settings → Staff first if needed)"
                required
              >
                <option value="">Select staff…</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {formatStaffSelectLabel(s)}
                  </option>
                ))}
              </select>
              {staff.length === 0 && (
                <span className="tenant-team-add-user-hint">Create staff under Settings → Staff first.</span>
              )}
              <button type="submit" className="btn btn-primary" disabled={saving || !addUserEmail.trim() || !addUserRoleId || !addUserStaffId}>
                Add
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowAddUser(false); setAddUserEmail(''); setAddUserRoleId(''); setAddUserStaffId(''); }}
                disabled={saving}
              >
                Cancel
              </button>
            </form>
          ) : null}
        </div>

      {error && (
        <div className="tenant-team-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="tenant-team-table-wrap">
        <table className="tenant-team-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Staff</th>
              <th>Roles</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">No users in this tenant.</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <span className="tenant-team-username">{user.username || user.email || '-'}</span>
                    {user.email && user.username !== user.email && (
                      <span className="tenant-team-email">{user.email}</span>
                    )}
                  </td>
                  <td>{user.staff_name || '-'}</td>
                  <td>
                    <ul className="tenant-team-roles">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((r) => (
                          <li key={r.role_id}>
                            <span>{r.role_name || r.role_code}</span>
                            <button
                              type="button"
                              className="tenant-team-remove-role"
                              onClick={() => handleRemoveRole(user.id, r.role_id)}
                              disabled={saving}
                              title="Remove this role"
                            >
                              ×
                            </button>
                          </li>
                        ))
                      ) : (
                        <li className="no-roles">No roles</li>
                      )}
                    </ul>
                  </td>
                  <td>
                    {addingForUser === user.id ? (
                      <div className="tenant-team-add-inline">
                        <select
                          value={selectedRoleId}
                          onChange={(e) => setSelectedRoleId(e.target.value)}
                          disabled={saving}
                        >
                          <option value="">Select role…</option>
                          {roles
                            .filter((r) => !(user.roles || []).some((ur) => ur.role_id === r.id))
                            .map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name || r.code}
                              </option>
                            ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => handleAddRole(user.id)}
                          disabled={saving || !selectedRoleId}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => { setAddingForUser(null); setSelectedRoleId(''); }}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="tenant-team-actions-btns">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setAddingForUser(user.id)}
                          disabled={saving}
                        >
                          + Add role
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm tenant-team-btn-perms"
                          onClick={() => handleViewEffectivePermissions(user)}
                          disabled={saving}
                          title="View effective permissions (union of all roles)"
                        >
                          View permissions
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {permsUser && (
        <div className="tenant-team-modal-overlay" onClick={closePermsModal}>
          <div className="tenant-team-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tenant-team-modal-header">
              <h3>Effective permissions – {permsUser.username || permsUser.email || permsUser.id}</h3>
              <button type="button" className="tenant-team-modal-close" onClick={closePermsModal} aria-label="Close">×</button>
            </div>
            <div className="tenant-team-modal-body">
              {permsLoading ? (
                <p>Loading…</p>
              ) : effectivePerms?.error ? (
                <p className="tenant-team-error-inline">{effectivePerms.error}</p>
              ) : effectivePerms ? (
                <>
                  <p className="tenant-team-perms-intro">Roles in this tenant (stacked):</p>
                  <ul className="tenant-team-perms-roles">
                    {(effectivePerms.roles || []).map((r, i) => (
                      <li key={i}>{r.role_name || r.role_code}</li>
                    ))}
                  </ul>
                  <p className="tenant-team-perms-intro">Effective permissions (union of all roles):</p>
                  <ul className="tenant-team-perms-list">
                    {(effectivePerms.permissions || []).map((p) => (
                      <li key={p.code}>
                        <code>{p.code}</code>
                        {p.name && <span> – {p.name}</span>}
                      </li>
                    ))}
                  </ul>
                  {(effectivePerms.permissions || []).length === 0 && (
                    <p className="no-data">No permissions assigned to these roles.</p>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantTeam;
